import {
  COMPANY_LEGAL_FIELDS,
  DOCUMENT_IMAGE_LABELS,
  ISSUER_FIELDS,
  type DocumentImageSlot,
  type DocumentSettings,
} from '@clim/domain';
import { useEffect, useState } from 'react';
import { ErrorBox, Field, Input } from '../../components/ui';
import { useSession } from '../../lib/session';
import { useApi, useMutation } from './common';

const toBase64 = (buf: ArrayBuffer) => {
  let bin = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
};

export function ImageSlot({
  slot,
  present,
  onChanged,
}: {
  slot: DocumentImageSlot;
  present: boolean;
  onChanged(): void;
}) {
  const m = useMutation();
  const [preview, setPreview] = useState<string | null>(null);
  const isLogo = slot === 'logo';
  const upload = async (file: File) => {
    if (isLogo ? !['image/png', 'image/jpeg'].includes(file.type) : file.type !== 'image/png')
      return m.setError(
        isLogo ? 'Format PNG ou JPEG attendu' : 'Format PNG attendu (idéalement sur fond transparent)',
      );
    if (file.size > 1024 * 1024) return m.setError('Image trop lourde (1 Mo maximum)');
    await m.run('PUT', `/v1/admin/documents/images/${slot}`, {
      dataBase64: toBase64(await file.arrayBuffer()),
    });
    setPreview(URL.createObjectURL(file));
    onChanged();
  };
  return (
    <div className="doc-image">
      <strong>{DOCUMENT_IMAGE_LABELS[slot]}</strong>
      <div className="doc-image-box">
        {preview ? (
          <img src={preview} alt={DOCUMENT_IMAGE_LABELS[slot]} />
        ) : (
          <span className="muted">{present ? 'Image en place ✓' : 'Aucune image'}</span>
        )}
      </div>
      <div className="actions">
        <label className="btn small">
          {present ? 'Remplacer' : isLogo ? 'Choisir une image' : 'Choisir un PNG'}
          <input
            type="file"
            accept={isLogo ? 'image/png,image/jpeg' : 'image/png'}
            hidden
            aria-label={`Fichier : ${DOCUMENT_IMAGE_LABELS[slot]}`}
            onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0]).catch(() => undefined)}
          />
        </label>
        {present && (
          <button
            className="btn small danger-outline"
            onClick={() =>
              void m.run('DELETE', `/v1/admin/documents/images/${slot}`).then(
                () => (setPreview(null), onChanged()),
                () => undefined,
              )
            }
          >
            Retirer
          </button>
        )}
      </div>
      <ErrorBox message={m.error} />
    </div>
  );
}

/**
 * Documents émis : tampon + signature du gérant (devis / factures, activables), et période « facturation au nom d'un
 * partenaire » : ses coordonnées remplacent les nôtres sur les devis, sans logo ; rien d'autre ne change.
 */
export function DocumentsCard() {
  const res = useApi<DocumentSettings>('/v1/admin/documents');
  const m = useMutation();
  const { refreshMe } = useSession();
  const [d, setD] = useState<DocumentSettings | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (res.data) setD(res.data);
  }, [res.data]);
  if (!d) return <ErrorBox message={res.error} />;
  const set = (patch: Partial<DocumentSettings>) => (setSaved(false), setD({ ...d, ...patch }));
  const setIdentity = (k: string, v: string) =>
    set({ partner: { ...d.partner, identity: { ...d.partner.identity, [k]: v } } });
  const save = async () => {
    await m.run('PUT', '/v1/admin/documents', {
      stampOnQuotes: d.stampOnQuotes,
      stampOnInvoices: d.stampOnInvoices,
      partner: d.partner,
    });
    setSaved(true);
    await refreshMe();
    res.reload();
  };
  const labelOf = (k: string) => COMPANY_LEGAL_FIELDS.find((f) => f.key === k)?.label ?? k;
  return (
    <div className="card form">
      <h2>Documents émis : tampon et signature</h2>
      <p className="muted">
        Le tampon de l’entreprise et la signature du gérant sont apposés ensemble, dans le cadre « Pour
        l’entreprise », à côté de la signature du client. PNG sur fond transparent conseillé (1 Mo maximum).
      </p>
      <div className="doc-images">
        <ImageSlot slot="stamp" present={!!d.images.stamp} onChanged={res.reload} />
        <ImageSlot slot="signature" present={!!d.images.signature} onChanged={res.reload} />
      </div>
      <label className="check">
        <input
          type="checkbox"
          checked={d.stampOnQuotes}
          onChange={(e) => set({ stampOnQuotes: e.target.checked })}
        />{' '}
        Sur les devis / bons de commande
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={d.stampOnInvoices}
          onChange={(e) => set({ stampOnInvoices: e.target.checked })}
        />{' '}
        Sur les factures
      </label>
      <p className="muted small">
        Les factures sont aujourd’hui établies dans votre logiciel de facturation (Facture.net) : ce réglage
        s’appliquera dès que Krotime produira les factures.
      </p>

      <h2>Devis au nom d’un partenaire</h2>
      <label className="check">
        <input
          type="checkbox"
          checked={d.partner.active}
          onChange={(e) => set({ partner: { ...d.partner, active: e.target.checked } })}
        />{' '}
        Émettre les devis au nom du partenaire (période exceptionnelle)
      </label>
      {d.partner.active && (
        <p className="warning-box">
          Tant que cette case est cochée, les nouveaux devis / bons de commande portent les coordonnées
          ci-dessous et <strong>aucun logo</strong>. Les devis déjà signés ne changent pas ; le reste de
          l’application est inchangé.
        </p>
      )}
      <div className="grid2">
        {ISSUER_FIELDS.map((k) => (
          <Field key={k} label={`${labelOf(k)} du partenaire`}>
            <Input value={d.partner.identity[k] ?? ''} onChange={(e) => setIdentity(k, e.target.value)} />
          </Field>
        ))}
      </div>
      <div className="doc-images">
        <ImageSlot slot="partnerStamp" present={!!d.images.partnerStamp} onChanged={res.reload} />
        <ImageSlot slot="partnerSignature" present={!!d.images.partnerSignature} onChanged={res.reload} />
      </div>
      <p className="muted small">
        Sur un devis au nom du partenaire, seul le tampon du partenaire est apposé (jamais le vôtre).
      </p>
      {saved && <p role="status">Réglages des documents enregistrés.</p>}
      <ErrorBox message={m.error} />
      <button className="btn primary" onClick={() => void save().catch(() => undefined)}>
        Enregistrer
      </button>
    </div>
  );
}
